<template>
  <div class="card transform-container" @click="abrirVaga(vaga)">
    <div class="situacao-main">
      <h6 class="situacao" :class="classeStatusEfetivo">
        {{ formatarStatus(statusEfetivo) }}
      </h6>
    </div>

    <img class="card-img-top" :src="vaga.acaoImagemUrl || imagemDefault" :alt="vaga.titulo || 'Imagem da vaga'" />

    <div class="card-body">
      <div class="card-head">
        <h5>
          {{ vaga.titulo }}
        </h5>

        <p id="acao-titulo" v-if="vaga.acaoTitulo">
          {{ vaga.acaoTitulo.replace(/"/g, "") }}
        </p>

        <div class="badges-row">
          <span class="badge-curricularizada" :class="vaga.curricularizada ? 'curricularizada' : 'regular'">
            {{ vaga.curricularizada ? "Ação curricular" : "Ação Regular" }}
          </span>
          <span class="badge-tipo-vaga" :class="obterClasseTipoVaga(vaga)">
            {{ formatarTipoVaga(vaga) }}
          </span>
        </div>
      </div>

      <div id="descricao" v-if="vaga.textoInformativo">
        <p class="card-text">
          {{ limitarTexto(removerHtml(vaga.textoInformativo, 120)) }}
        </p>
      </div>

      <div id="infos">
        <p class="card-text" v-if="vaga.localVaga">
          <strong>Local:</strong> {{ vaga.localVaga }}
        </p>
        <p class="card-text" v-if="vaga.dataInicioInscricao && vaga.dataFimInscricao">
          <strong>Inscrições:</strong> de {{ formatarData(vaga.dataInicioInscricao) }} a
          {{ formatarData(vaga.dataFimInscricao) }}
        </p>
      </div>

      <div class="palavras-chave" v-if="vaga.tags && vaga.tags.length">
        <div class="palavra-chave" v-for="(palavra, index) in vaga.tags.slice(0, 3)" :key="index">
          {{ palavra }}
        </div>
        <div class="palavra-chave" v-if="vaga.tags.length > 3">
          +{{ vaga.tags.length - 3 }}
        </div>
      </div>
    </div>

    <div class="botoes" @click.stop>
      <button v-if="podeEditar" type="button" @click.stop="editarVaga(vaga)" class="btn-card btn-editar-card editar">
        <i class="ti ti-edit"></i>
        Editar vaga
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import {
  formatarData,
  limitarTexto,
  removerHtml,
} from "@/utils/FormatarEntradas.js";
import defaultImage from "@/assets/855f9e51e2bdc1674b356fcfd4b16a9d6c34d5ed.png";

const imagemDefault = defaultImage;

const props = defineProps({
  vaga: {
    type: Object,
    required: true,
  },
  podeEditar: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["abrir", "editar"]);

function abrirVaga(vaga) {
  emit("abrir", vaga);
}

function editarVaga(vaga) {
  emit("editar", vaga);
}

function obterStatusEfetivo(v) {
  if (["CANCELADA", "ENCERRADA"].includes(v.status)) {
    return v.status;
  }
  const agora = new Date();
  const inicio = new Date(v.dataInicioInscricao);
  const fim = new Date(v.dataFimInscricao);
  if (agora < inicio) return "BREVE";
  if (agora > fim) return "ENCERRADA";
  return "ABERTA";
}

const statusEfetivo = computed(() => obterStatusEfetivo(props.vaga));

const classeStatusEfetivo = computed(() => {
  const st = statusEfetivo.value;
  return {
    "inscricoes-abertas": st === "ABERTA",
    programada: st === "BREVE",
    encerrada: st === "ENCERRADA",
    cancelada: st === "CANCELADA",
  };
});

function formatarStatus(status) {
  switch (status) {
    case "ABERTA":
      return "Inscrições Abertas";
    case "BREVE":
      return "Inscrições em Breve";
    case "ENCERRADA":
      return "Inscrições Encerradas";
    case "CANCELADA":
      return "Cancelada";
    default:
      return status;
  }
}

function formatarTipoVaga(v) {
  if (v.isEstagiario) return "Estágio";
  const remunerada = v.isBolsistaRemunerado ?? v.remunerada ?? false;
  return remunerada ? "Remunerada" : "Agente";
}

function obterClasseTipoVaga(v) {
  if (v.isEstagiario) return "tipo-estagio";
  const remunerada = v.isBolsistaRemunerado ?? v.remunerada ?? false;
  return remunerada ? "tipo-remunerada" : "tipo-agente";
}
</script>

<style scoped>
.card {
  background-color: transparent;
  overflow: hidden;
  border-radius: 10px;
  width: 100%;
  height: 235px;
  max-height: 235px;
  display: flex;
  align-items: stretch;
  flex-direction: row;
  gap: 12px;
  position: relative;
  cursor: pointer;
  transition: box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid hsla(0, 0%, 0%, 0.3);
}

.card:hover {
  transform: translateY(-2px);
  border-color: var(--roxo-claro);
  box-shadow:
    0 10px 24px -3px hsla(258, 53%, 28%, 0.14),
    0 4px 10px -2px hsla(258, 53%, 28%, 0.06);
}

p {
  margin: 0;
}

.card-img-top {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
  height: 215px;
  object-fit: cover;
  background-size: cover;
  margin-top: 10px;
  border-radius: 12px;
  flex-shrink: 0;
  border: 1px solid var(--roxo-superficie);
}

.card-body {
  padding: 8px 12px 8px 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 235px;
  max-height: 235px;
  gap: 5px;
  overflow: hidden;
}

.card-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

#acao-titulo {
  font-size: 13px;
  color: var(--preto);
  font-weight: 500;
  margin-bottom: 2px;
}

.card-head p,
#infos p,
#descricao p {
  margin: 0;
  padding: 0;
  line-height: 1.38;
  font-size: 14px;
  color: var(--preto);
}

#descricao strong,
#infos strong {
  color: var(--roxo-primario);
}

.situacao-main {
  position: relative;
  width: 44px;
  height: 100%;
  align-self: stretch;
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-shrink: 0;
}

.situacao {
  color: var(--branco);
  margin: 0;
  padding: 10px 4px;
  width: 100%;
  height: 100%;
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  font-size: 0.95rem;
  font-weight: 600;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

}

.programada {
  background-color: var(--azul-claro);
}

.encerrada {
  background-color: var(--cinza-escuro);
}

.cancelada {
  background-color: var(--vermelho);
}

.inscricoes-abertas {
  background-color: var(--roxo-medio);
}

.em-execucao {
  background-color: var(--laranja);
}

h5 {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--roxo-primario);
  margin: 0 0 2px 0;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.badges-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.badge-curricularizada,
.badge-tipo-vaga {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.badge-curricularizada.curricularizada {
  background: var(--verde-claro);
  color: var(--verde-escuro);
  border: 1px solid var(--verde-claro);
}

.badge-curricularizada.regular {
  background: var(--cinza-claro);
  color: var(--cinza-medio);
  border: 1px solid var(--cinza-claro);
}

.tipo-agente {
  background: var(--laranja-claro);
  color: var(--laranja-escuro);
  border: 1px solid var(--laranja-claro);
}

.tipo-remunerada {
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  border: 1px solid var(--cinza-medio);
}

.tipo-estagio {
  background: var(--roxo-superficie);
  color: var(--roxo-primario);
  border: 1px solid var(--roxo-claro);
}

#infos {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  font-size: 14px;
  word-wrap: break-word;
  flex-shrink: 0;
  margin-top: 2px;
}

#descricao {
  margin: 0;
  overflow: hidden;
  min-height: 0;
  flex-shrink: 1;
}

#descricao .card-text {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.palavras-chave {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 6px;
}

.palavra-chave {
  background-color: var(--roxo-superficie);
  color: var(--roxo-primario);
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.botoes {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

.btn-card {
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px hsla(0, 0%, 0%, 0.08);
  user-select: none;
  line-height: 1.4;
}

.btn-card:hover {
  transform: translateY(-1px);
}

.btn-editar-card,
.editar {
  background-color: var(--roxo-primario);
  color: var(--branco);
}

.btn-editar-card:hover,
.editar:hover {
  background-color: var(--roxo-medio);
}

@media (max-width: 600px) {
  .card {
    flex-direction: column;
    height: auto;
    max-height: none;
    gap: 8px;
  }

  .situacao-main {
    width: 100%;
    height: auto;
  }

  .situacao {
    writing-mode: horizontal-tb;
    transform: none;
    padding: 6px 10px;
    border-radius: 10px 10px 0 0;
  }

  .card-img-top {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: 180px;
    max-height: 180px;
    margin-top: 0;
  }

  .card-body {
    height: auto;
    max-height: none;
    padding: 8px 12px 48px;
  }
}
</style>
